//src/web/pages/Presentation/LocationCollection.tsx

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../../shared/api/axios';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import LazyVideoPreview from "./LazyVideoPreview";
import { List, RowComponentProps } from "react-window";

export type MediaEntry = {
  kind: 'image' | 'video';

  // image 
  url?: string;

  // new video pipeline 
  isHls?: boolean;
  videoId?: string;
  hlsUrl?: string | null;
  dashUrl?: string | null;
  posterUrl?: string | null;

  // music
  musicId?: string | null;
  musicTitle?: string | null;
  musicArtist?: string | null;
};

type Usage = {
  _id?: string;
  postId?: string;
  createdAt: string;
  userDetails?: {
    _id: string;
    profilename?: string;
    picture?: string | null;
  } | null;
  location?: {
    city?: string;
    country?: string;
    countryCode?: string;
  };
  likes?: number;
  likedByUser?: boolean;
  media?: MediaEntry[];
};

type LocationRowProps = {
  rows: Usage[][];
  navigate: ReturnType<typeof useNavigate>;
  authUserId?: string;
  t: (key: string, options?: any) => string;
};

// Api base URL normalization
const API_BASE = process.env.REACT_APP_API_BASE_URL as string;
if (!API_BASE) {
  throw new Error("REACT_APP_API_BASE_URL is not defined");
}

const API_ORIGIN = API_BASE.replace(/\/api(\/)?$/, "").replace(/\/$/, "");

const hlsUrl = (videoId: string) =>
  `${API_ORIGIN}/hls/playlist/${videoId}`;

const resolveVideoUrl = (m: MediaEntry): string => {
  if (m.hlsUrl) return m.hlsUrl;
  if (m.videoId) return hlsUrl(m.videoId);
  if (m.url && /^https?:\/\//i.test(m.url)) return m.url; // legacy fallback
  return "";
};

const mediaHeight = 603;
const COLUMN_COUNT = 4;
const ROW_HEIGHT = mediaHeight + 120;

//# of Posts in one feed ... first posts get deleted when I scroll so only upon reload available again
const MAX_ITEMS = 700;

function LocationRow({
  index,
  style,
  rows,
  navigate,
  authUserId,
  t,
}: RowComponentProps<LocationRowProps>) {
  const rowItems = rows[index];

  return (
    <div
      style={{
        ...style,
        display: "grid",
        gridTemplateColumns: `repeat(${COLUMN_COUNT}, 1fr)`,
        gap: 30,
        width: "100%",
        maxWidth: 1400,
        margin: "0 auto",
        boxSizing: "border-box",
        paddingBottom: 14,
        alignItems: "start",
      }}
    >
      {rowItems.map((u) => {
        const user = u.userDetails;
        const userDetails = u.userDetails;
        const userName = user?.profilename || t('gallery.post.unknownUser');
        const userHasPic =
          typeof user?.picture === 'string' && user.picture !== '';

        const previewMedia =
          Array.isArray(u.media) && u.media.length > 0 ? u.media[0] : null;

        const previewVideoUrl =
          previewMedia?.kind === 'video' ? resolveVideoUrl(previewMedia) : '';

        const previewVideoInstanceId =
          previewMedia?.kind === "video"
            ? `${u.postId || u._id || "unknown"}-preview`
            : null;

        const userPicStyle = userHasPic
          ? {
              backgroundImage: `url(${user.picture})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              fontSize: 0,
            }
          : {
              backgroundColor: '#261da2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18,
            };

        return (
          <div key={u.postId || u._id} style={styles.pin}>
            <div style={styles.pinMeta}>
              <div style={styles.userLinkWrapper}>
                <div
                  onClick={() => {
                    if (!userDetails?._id) return;
                    const isOwnProfile = authUserId === userDetails._id;
                    navigate(isOwnProfile ? "/profile" : `/visitor-profile/${userDetails._id}`);
                  }}
                  style={{ ...styles.userLink, cursor: "pointer" }}
                  title={userName}
                >
                  <div style={{ ...styles.userPic, ...userPicStyle }}>
                    {!userHasPic && "👻"}
                  </div>
                  <span>{userName}</span>
                </div>
              </div>

              <button
                onClick={() => {
                  const resolvedPostId = u.postId || u._id;
                  if (!resolvedPostId) return;
                  navigate(`/location-post-detail/${encodeURIComponent(resolvedPostId)}`);
                }}
                style={styles.pinLink}
              >
                {t('locationCollection.openPostLabel')}
              </button>
            </div>

            <div style={styles.mediaWrap}>
              {previewMedia ? (
                previewMedia.kind === 'video' && previewVideoUrl ? (
                  <div style={styles.videoContainer}>
                    <LazyVideoPreview
                      src={previewVideoUrl}
                      poster={previewMedia.posterUrl}
                      style={styles.previewVideo}
                      videoId={previewVideoInstanceId!}
                    />
                  </div>
                ) : (
                  <img
                    src={previewMedia.url}
                    alt={t('gallery.media.previewAlt')}
                    style={styles.previewImage}
                    loading="lazy"
                    decoding="async"
                  />
                )
              ) : (
                <div>{t('gallery.post.noMedia')}</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}


export default function LocationCollectionPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const { user: authUser } = useAuth();

  const [usages, setUsages] = useState<Usage[]>([]);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const LIMIT = 20;
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadingRef = React.useRef(false);
  const search = new URLSearchParams(location.search);
  const city = search.get('city') || '';
  const country = search.get('country') || '';
  const countryCode = search.get('countryCode') || '';

  const normalizedCountryCode = countryCode.toUpperCase();

const translatedCountry =
  normalizedCountryCode
    ? t(`countries.${normalizedCountryCode}`, {
        defaultValue: country || normalizedCountryCode,
      })
    : country || t('locationCollection.unknownCountry');

    const [listHeight, setListHeight] = useState(
  Math.max(300, window.innerHeight - 170)
);


useEffect(() => {
  const onResize = () => {
    setListHeight(Math.max(300, window.innerHeight - 170));
  };

  window.addEventListener("resize", onResize);
  return () => window.removeEventListener("resize", onResize);
}, []);



  //Loading effect
  useEffect(() => {
  const styleId = 'spin-keyframes';

  if (document.getElementById(styleId)) return;

  const style = document.createElement('style');
  style.id = styleId;
  style.innerHTML = `
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `;

  document.head.appendChild(style);
  return () => style.remove();
}, []);

//Fetching Location Posts
useEffect(() => {
 if (!city || !(countryCode || country)) {
  setUsages([]);
  setOffset(0);
  setHasMore(false);
  setLoading(false);
  setLoadingMore(false);
  loadingRef.current = false;
  return;
}
  setUsages([]);
  setOffset(0);
  setHasMore(true);
  setLoading(true);

  (async () => {
    try {
      const res = await api.get('/location/posts', {
        params: {
          city,
          country: country || undefined,
          countryCode: countryCode || undefined,
          offset: 0,
          limit: LIMIT,
        },
      });

      const items = Array.isArray(res.data?.posts) ? res.data.posts : [];

      setUsages(items);
      setOffset(items.length);
      setHasMore(!!res.data?.hasMore);
    } catch (e) {
      console.error('Error fetching location posts:', e);
      setUsages([]);
      setHasMore(false);
      alert(t('locationCollection.loadError'));
    } finally {
      setLoading(false);
    }
  })();
}, [city, country, countryCode]);

const loadMore = async () => {
  if (!city || !(countryCode || country) || !hasMore || loadingMore || loadingRef.current) {
    return;
  }

  loadingRef.current = true;

  try {
    setLoadingMore(true);

    const res = await api.get('/location/posts', {
      params: {
        city,
        country: country || undefined,
        countryCode: countryCode || undefined,
        offset,
        limit: LIMIT,
      },
    });

    const newUsages = Array.isArray(res.data?.posts) ? res.data.posts : [];

 setUsages(prev => {
  const map = new Map(
    prev.map((p: any) => [p.postId || p._id || '', p])
  );

  for (const item of newUsages) {
    const key = item.postId || item._id || '';
    if (!key) continue;
    map.set(key, item);
  }

  const merged = Array.from(map.values());

  return merged.slice(-MAX_ITEMS); // keep only last 1000 posts
});

    setOffset(prev => prev + newUsages.length);
    setHasMore(!!res.data?.hasMore);
  } catch (e) {
    console.error('Location pagination load failed:', e);
  } finally {
    loadingRef.current = false;
    setLoadingMore(false);
  }
};

const rows = useMemo(() => {
  const chunked: Usage[][] = [];
  for (let i = 0; i < usages.length; i += COLUMN_COUNT) {
    chunked.push(usages.slice(i, i + COLUMN_COUNT));
  }
  return chunked;
}, [usages]);

 if (loading) {
  return (
    <div
      style={{
        height: '100dvh',
        width: '100%',
        background: '#050913',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          border: '3px solid rgba(255,255,255,0.15)',
          borderTop: '3px solid #fff',
          animation: 'spin 0.9s linear infinite',
        }}
      />
    </div>
  );
}

  return (
    <div style={styles.page}>
      {/* Close button */}
      <button
        onClick={() => navigate(-1)}
        aria-label={t('musicDetail.close')}
        title={t('musicDetail.close')}
        style={styles.closeBtn}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {/* Header */}
   <div style={styles.headerCard}>
  <div style={styles.coverWrap}>
    <div style={styles.coverFallback}>📍</div>
  </div>

  <div style={{ minWidth: 0 }}>
    <h1 style={styles.title}>{city || t('locationCollection.unknownCity')}</h1>
    <div style={styles.countryText}>
      {translatedCountry}
    </div>
    <div style={styles.countHeading}>
      {t('locationCollection.posts', { count: usages.length })}
    </div>
  </div>
</div>
      {/* Display posts that use this music */}
      {usages.length === 0 ? (
       <div style={styles.empty}>{t('locationCollection.empty')}</div>
      ) : (
<List
  style={{ height: listHeight, width: "100%" }}
  rowCount={rows.length}
  rowHeight={ROW_HEIGHT}
  rowComponent={LocationRow}
  rowProps={{
    rows,
    navigate,
    authUserId: authUser?._id,
    t,
  }}
  overscanCount={3}
  onRowsRendered={({ stopIndex }) => {
    if (!hasMore || loadingMore || loadingRef.current) return;

    if (stopIndex >= rows.length - 3) {
      loadMore();
    }
  }}
/>

      )}
      {loadingMore && (
  <div style={{ display: 'flex', justifyContent: 'center', padding: 30 }}>
    <div
      style={{
        width: 34,
        height: 34,
        borderRadius: '50%',
        border: '3px solid rgba(255,255,255,0.15)',
        borderTop: '3px solid #fff',
        animation: 'spin 0.9s linear infinite',
      }}
    />
  </div>
)}
    </div>
  );
}


const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    width: '100%',
    background: '#050913',
    color: '#e6e9f3',
    padding: '20px 18px',
    paddingTop: 32,
    boxSizing: 'border-box',
  },

  // Close button: no border now
  closeBtn: {
    position: 'fixed',
    top: 14,
    right: 18,
    zIndex: 60,
    width: 34,
    height: 34,
    display: 'grid',
    placeItems: 'center',
    border: 'none', // <-- removed border
    borderRadius: 999,
    background:
      'radial-gradient(circle at 0% 0%, rgba(255,255,255,0.12), rgba(10,10,12,0.95))',
    color: '#e6e9f3',
    backdropFilter: 'blur(10px)',
    cursor: 'pointer',
    transition: 'transform 120ms ease, box-shadow 120ms ease',
    boxShadow: '0 8px 18px rgba(0,0,0,0.45)',
  },

  // Header card narrower, especially on the right side
  headerCard: {
    display: 'grid',
    gridTemplateColumns: '72px 1fr',
    gap: 14,
    padding: 14,
    borderRadius: 12,
    background:
      'linear-gradient(155deg, rgba(255,255,255,0.06), rgba(10,10,16,0.9))',
    border: '1px solid rgba(255,255,255,0.12)',
    boxShadow: '0 12px 32px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.05)',
    marginBottom: 18,
    maxWidth: 1300,            // <-- limits width, so right side isn't so wide
    marginRight: 'auto',      // align to the left
  },

  coverWrap: {
    width: 72,
    height: 72,
    borderRadius: 12,
    overflow: 'hidden',
    background: '#000',
    display: 'grid',
    placeItems: 'center',
  },
  
  coverFallback: { fontSize: 28, opacity: 0.9 },

  title: {
    fontSize: 22,
    fontWeight: 800,
    margin: 0,
    lineHeight: 1.15,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  
  videoContainer: {
    position: 'relative',
    width: '100%',
    height: mediaHeight,
    overflow: 'hidden',
    borderRadius: 12,
    background: '#000',
    marginBottom: 6,
},

  pin: {
    display: 'inline-block',
    width: '100%',
    marginBottom: 14,
    breakInside: 'avoid',
    background: 'rgba(255,255,255,0.02)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 12,
    boxShadow: '0 6px 16px rgba(0,0,0,0.25)',
    overflow: 'hidden',
    alignSelf: 'start', 
  },

  mediaWrap: {
    position: 'relative',
    width: '100%',
    height: mediaHeight,
    overflow: 'hidden',
    background: '#000',
    borderRadius: 12,
    marginBottom: 6,
  },

  previewImage: {
    width: '100%',
    height: mediaHeight,
    objectFit: 'cover',
    display: 'block',
    borderRadius: 12,
  },

  previewVideo: {
    width: '100%',
    height: mediaHeight,
    objectFit: 'cover',
    display: 'block',
    borderRadius: 12,
    background: '#000',
  },

  pinMeta: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    padding: '8px 10px',
  },

  pinLink: {
    fontSize: 12,
    border: '1px solid rgba(255,255,255,0.18)',
    background: 'rgba(255,255,255,0.06)',
    color: '#e6e9f3',
    padding: '4px 8px',
    borderRadius: 8,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },

  userLinkWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },

  userLink: {
    display: 'flex',
    alignItems: 'center',
    textDecoration: 'none',
    color: '#cfd6ec',
    fontSize: 13,
    fontWeight: 500,
    gap: 8,
  },

  userPic: {
    width: 30,
    height: 30,
    borderRadius: '5%',
    objectFit: 'cover',
    backgroundColor: '#1a1a1a',
    flexShrink: 0,
  },
  
  countHeading: {
    fontSize: 13,
    color: '#cfd6ec',
    marginTop: 6,
  },

  countryText: {
    fontSize: 13,
    color: '#a9b0c6',
    marginTop: 2,
  },

  empty: { color: '#9aa3bd' },

};


